import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RegisterData, ResetPasswordData } from '../utils/auth';
import './AuthDialog.css';

export type AuthMode = 'login' | 'register' | 'forgot-password' | 'otp-verification' | 'reset-password';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
  onAuthSuccess?: () => void;
}

const AuthDialog: React.FC<AuthDialogProps> = ({
  isOpen,
  onClose,
  initialMode = 'login',
  onAuthSuccess
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    phone: '',
    otp: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { login, register, forgotPassword, verifyOtp, resetPassword } = useAuth();

  // Reset form when mode changes or dialog opens/closes
  React.useEffect(() => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      phone: '',
      otp: ''
    });
    setErrors({});
  }, [mode, isOpen]);

  // Update mode when initialMode prop changes or dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [initialMode, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === 'register') {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      // Phone is optional, but if provided, validate length (10-15 characters as per backend)
      if (formData.phone && formData.phone.trim()) {
        const phone = formData.phone.trim();
        if (phone.length < 10 || phone.length > 15) {
          newErrors.phone = 'Phone number must be between 10 and 15 characters';
        }
      }
    } else if (mode === 'login') {
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
    } else if (mode === 'forgot-password') {
      if (!formData.email.trim()) newErrors.email = 'Email is required';
    } else if (mode === 'otp-verification') {
      if (!formData.otp.trim()) newErrors.otp = 'OTP is required';
      if (formData.otp.length !== 6) newErrors.otp = 'OTP must be 6 digits';
    } else if (mode === 'reset-password') {
      if (!formData.newPassword) newErrors.newPassword = 'New password is required';
      if (!formData.confirmNewPassword) newErrors.confirmNewPassword = 'Please confirm your new password';
      if (formData.newPassword !== formData.confirmNewPassword) {
        newErrors.confirmNewPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      let response;

      switch (mode) {
        case 'login':
          response = await login(formData.email, formData.password);
          break;
        case 'register':
          const registerData: RegisterData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
            confirmPassword: formData.confirmPassword,
            phone: formData.phone.trim() || undefined
          };
          response = await register(registerData);
          break;
        case 'forgot-password':
          response = await forgotPassword(formData.email);
          break;
        case 'otp-verification':
          response = await verifyOtp(formData.otp);
          break;
        case 'reset-password':
          const resetData: ResetPasswordData = {
            newPassword: formData.newPassword,
            confirmNewPassword: formData.confirmNewPassword
          };
          response = await resetPassword(resetData);
          break;
        default:
          throw new Error('Invalid mode');
      }

      if (response.success) {
        if (mode === 'login' || mode === 'register') {
          onAuthSuccess?.();
          onClose();
        } else if (mode === 'forgot-password') {
          setMode('otp-verification');
        } else if (mode === 'otp-verification') {
          setMode('reset-password');
        } else if (mode === 'reset-password') {
          setMode('login');
        }
      } else {
        // Handle specific field errors
        if (response.message.includes('email')) {
          setErrors({ email: response.message });
        } else if (response.message.includes('password')) {
          setErrors({ password: response.message });
        } else {
          setErrors({ general: response.message });
        }
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'login': return 'Welcome Back';
      case 'register': return 'Join CSZGLOBAL';
      case 'forgot-password': return 'Reset Password';
      case 'otp-verification': return 'Verify OTP';
      case 'reset-password': return 'New Password';
      default: return 'Authentication';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'login': return 'Sign in to your casino builder account';
      case 'register': return 'Create your casino empire in minutes';
      case 'forgot-password': return 'Enter your email to receive reset instructions';
      case 'otp-verification': return 'Enter the 6-digit code sent to your email';
      case 'reset-password': return 'Create a new password for your account';
      default: return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-dialog-overlay" onClick={onClose}>
      <div className="auth-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="auth-dialog__header">
          <button className="auth-dialog__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="auth-dialog__logo">
            <span className="logo-text">CSZGLOBAL</span>
          </div>
        </div>

        <div className="auth-dialog__content">
          <div className="auth-dialog__title-section">
            <h2 className="auth-dialog__title">{getTitle()}</h2>
            <p className="auth-dialog__subtitle">{getSubtitle()}</p>
          </div>

          {errors.general && (
            <div className="auth-dialog__error auth-dialog__error--general">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-dialog__form">
            {mode === 'register' && (
              <>
                <div className="auth-dialog__field">
                  <label htmlFor="firstName" className="auth-dialog__label">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`auth-dialog__input ${errors.firstName ? 'auth-dialog__input--error' : ''}`}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && <span className="auth-dialog__error">{errors.firstName}</span>}
                </div>
                <div className="auth-dialog__field">
                  <label htmlFor="lastName" className="auth-dialog__label">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`auth-dialog__input ${errors.lastName ? 'auth-dialog__input--error' : ''}`}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && <span className="auth-dialog__error">{errors.lastName}</span>}
                </div>
              </>
            )}

            {(mode === 'login' || mode === 'register' || mode === 'forgot-password') && (
              <div className="auth-dialog__field">
                <label htmlFor="email" className="auth-dialog__label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`auth-dialog__input ${errors.email ? 'auth-dialog__input--error' : ''}`}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="auth-dialog__error">{errors.email}</span>}
              </div>
            )}

            {mode === 'register' && (
              <div className="auth-dialog__field">
                <label htmlFor="phone" className="auth-dialog__label">Phone Number (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`auth-dialog__input ${errors.phone ? 'auth-dialog__input--error' : ''}`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && <span className="auth-dialog__error">{errors.phone}</span>}
                <p className="auth-dialog__hint">Phone number must be between 10 and 15 characters</p>
              </div>
            )}

            {(mode === 'login' || mode === 'register') && (
              <div className="auth-dialog__field">
                <label htmlFor="password" className="auth-dialog__label">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`auth-dialog__input ${errors.password ? 'auth-dialog__input--error' : ''}`}
                  placeholder="Enter your password"
                />
                {errors.password && <span className="auth-dialog__error">{errors.password}</span>}
              </div>
            )}

            {mode === 'register' && (
              <div className="auth-dialog__field">
                <label htmlFor="confirmPassword" className="auth-dialog__label">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`auth-dialog__input ${errors.confirmPassword ? 'auth-dialog__input--error' : ''}`}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && <span className="auth-dialog__error">{errors.confirmPassword}</span>}
              </div>
            )}

            {mode === 'otp-verification' && (
              <div className="auth-dialog__field">
                <label htmlFor="otp" className="auth-dialog__label">OTP Code</label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleInputChange}
                  className={`auth-dialog__input ${errors.otp ? 'auth-dialog__input--error' : ''}`}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
                {errors.otp && <span className="auth-dialog__error">{errors.otp}</span>}
                <p className="auth-dialog__hint">Default OTP: 123456</p>
              </div>
            )}

            {mode === 'reset-password' && (
              <>
                <div className="auth-dialog__field">
                  <label htmlFor="newPassword" className="auth-dialog__label">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className={`auth-dialog__input ${errors.newPassword ? 'auth-dialog__input--error' : ''}`}
                    placeholder="Enter new password"
                  />
                  {errors.newPassword && <span className="auth-dialog__error">{errors.newPassword}</span>}
                </div>
                <div className="auth-dialog__field">
                  <label htmlFor="confirmNewPassword" className="auth-dialog__label">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    value={formData.confirmNewPassword}
                    onChange={handleInputChange}
                    className={`auth-dialog__input ${errors.confirmNewPassword ? 'auth-dialog__input--error' : ''}`}
                    placeholder="Confirm new password"
                  />
                  {errors.confirmNewPassword && <span className="auth-dialog__error">{errors.confirmNewPassword}</span>}
                </div>
              </>
            )}

            <button
              type="submit"
              className="auth-dialog__submit"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : getSubmitButtonText()}
            </button>
          </form>

          <div className="auth-dialog__footer">
            {mode === 'login' && (
              <>
                <button
                  type="button"
                  className="auth-dialog__link"
                  onClick={() => setMode('forgot-password')}
                >
                  Forgot Password?
                </button>
                <p className="auth-dialog__footer-text">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="auth-dialog__link"
                    onClick={() => setMode('register')}
                  >
                    Sign Up
                  </button>
                </p>
              </>
            )}

            {mode === 'register' && (
              <p className="auth-dialog__footer-text">
                Already have an account?{' '}
                <button
                  type="button"
                  className="auth-dialog__link"
                  onClick={() => setMode('login')}
                >
                  Sign In
                </button>
              </p>
            )}

            {mode === 'forgot-password' && (
              <p className="auth-dialog__footer-text">
                Remember your password?{' '}
                <button
                  type="button"
                  className="auth-dialog__link"
                  onClick={() => setMode('login')}
                >
                  Sign In
                </button>
              </p>
            )}

            {mode === 'otp-verification' && (
              <p className="auth-dialog__footer-text">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  className="auth-dialog__link"
                  onClick={() => setMode('forgot-password')}
                >
                  Resend
                </button>
              </p>
            )}

            {mode === 'reset-password' && (
              <p className="auth-dialog__footer-text">
                Remember your password?{' '}
                <button
                  type="button"
                  className="auth-dialog__link"
                  onClick={() => setMode('login')}
                >
                  Sign In
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function getSubmitButtonText(): string {
    switch (mode) {
      case 'login': return 'Sign In';
      case 'register': return 'Create Account';
      case 'forgot-password': return 'Send Reset Code';
      case 'otp-verification': return 'Verify Code';
      case 'reset-password': return 'Reset Password';
      default: return 'Submit';
    }
  }
};

export default AuthDialog;
